import {
  PaymentMethodType,
  StripeCard,
  StripePaymentMethodRequest,
} from '../../clients/quipli-service/payment/payment';
import { ReactNode, useEffect, useState } from 'react';
import { usePaymentApiV1 } from '../../clients/quipli-service/payment/payment-api-v1';
import { toast } from '../../util/toasts';
import { Spinner } from '../../components/spinner/spinner';
import { DateTime } from 'luxon';
import Icon from '../../components/icons/icon';
import { Radio } from '../../components/radio/radio';
import { ConfirmModal } from '../../components/modal/confirm-modal';
import { Typography } from '../../components/typography/typography';
import { Button } from '../../components/form/button';
import StripeSetupInit from './stripe/setup-component/stripe-setup-init';

export const CardPaymentMethodInput = ({
  userId,
  paymentMethod,
  setPaymentMethod,
  setCard,
  selectMode = true,
}: {
  userId: number;
  paymentMethod?: StripePaymentMethodRequest;
  setPaymentMethod: (pm: StripePaymentMethodRequest) => void;
  setCard?: (card: StripeCard) => void;
  selectMode?: boolean;
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [userCards, setUserCards] = useState<StripeCard[]>([]);
  const [paymentCard, setPaymentCard] = useState<StripeCard | null>(null);
  const [selectionForm, setSelectionForm] = useState<'cardSelect' | 'stripeSetup' | 'stripeSetupEdit'>('cardSelect');
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<ReactNode>();

  const { deleteCardAdmin, getCardsForUser, getCardById } = usePaymentApiV1();

  useEffect(() => {
    loadUserCards();
  }, [userId, selectionForm]);

  useEffect(() => {
    if (paymentCard) {
      setCard && setCard(paymentCard);
      setPaymentMethod({
        type: PaymentMethodType.Stripe,
        cardId: paymentCard.id,
        confirmationTokenId:
          paymentCard.stripeCardId == '' || !paymentCard.stripeCardId ? paymentCard.confirmationTokenId : undefined,
      });
    }
  }, [paymentCard]);

  const loadUserCards = async () => {
    setIsLoading(true);
    try {
      const [activeCards, defaultCard] = await Promise.all([
        getCardsForUser(userId),
        paymentMethod?.cardId && !paymentMethod.confirmationTokenId
          ? getCardById(paymentMethod.cardId)
          : Promise.resolve(),
      ]);

      if (activeCards.length === 0 && !defaultCard) {
        setSelectionForm('stripeSetup');
      } else {
        let cards = activeCards;
        if (defaultCard && !activeCards.some((c) => c.id === defaultCard.id)) {
          cards = [defaultCard, ...activeCards];
        }
        setUserCards(cards);
        setPaymentCard(defaultCard || cards[0]);
      }
    } catch (error) {
      toast('Failed to load user cards', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOnDelete = async (cardId: number) => {
    try {
      await deleteCardAdmin(cardId, userId);
      toast('Card removed.', 'success');
      setDeleteConfirmModal(undefined);
      loadUserCards();
    } catch (error) {
      toast('Error removing card.', 'error');
    }
  };

  const CardSelect = () => (
    <>
      {isLoading ? (
        <Spinner />
      ) : (
        <div className="card-list">
          {userCards.map((card, index) => {
            let disabled = false;
            let disabledLabel: string | undefined = undefined;
            if (card.lockTimestamp) {
              const duration = DateTime.now().diff(card.lockTimestamp, ['minutes', 'seconds']);
              // past
              disabled = duration.minutes <= 30 && duration.minutes > 0;
              disabledLabel = card.lockTimestamp.plus({ minutes: 30 }).toFormat('HH:mm');
            }
            let icon = undefined;
            const iconProps = {
              color: 'grey',
            };
            switch (card.brand.toLowerCase().trim()) {
              case 'visa':
                icon = <Icon icon={'VisaCardLogo'} size={'xlarge'} {...iconProps}></Icon>;
                break;
              case 'mastercard':
                icon = <Icon icon={'MasterCardLogo'} size={'xlarge'} {...iconProps}></Icon>;
                break;
              case 'amex':
              case 'americanexpress':
                icon = <Icon icon={'AmericanExpressLogo'} size={'xlarge'} {...iconProps}></Icon>;
                break;
            }

            const cardLabel = (
              <div key={`card-user-title-${index}`} className="flex justify-between grow gap-2" tabIndex={0}>
                <div className="flex items-center gap-2">
                  <span>
                    {card.nickname ? card.nickname + ' ' : ''}
                    {card.cardholderName} •••• {card.last4}
                  </span>
                  {icon}
                </div>
                <div className="flex items-center gap-2">
                  {!card.deletedAt && !disabled && (
                    <div
                      className="cursor-pointer text-[var(--link-200)]"
                      onClick={() => {
                        setPaymentCard(card);
                        setSelectionForm('stripeSetupEdit');
                      }}
                    >
                      Edit
                    </div>
                  )}
                  <div
                    className="cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      setDeleteConfirmModal(
                        <ConfirmModal
                          prompt="Remove card?"
                          additionalText="This cannot be undone"
                          noLabel="Cancel"
                          yesLabel="Remove"
                          yesButtonYellow
                          isOpen={true}
                          headerIcon={
                            <div className="flex justify-center">
                              <Icon icon="InfoCircle" size="xlarge" stroke="var(--error-500)" />
                            </div>
                          }
                          onConfirm={() => handleOnDelete(card.id)}
                          close={() => setDeleteConfirmModal(undefined)}
                          zIndex={10000}
                        />,
                      );
                    }}
                  >
                    <Icon icon="Trash" />
                  </div>
                </div>
              </div>
            );

            return (
              <div key={`card-user-body-${index}`} className="" tabIndex={1}>
                {!disabled ? (
                  selectMode ? (
                    <Radio
                      isChecked={!disabled && paymentCard?.id === card.id}
                      name={`payment_card_${index}`}
                      onChange={(isChecked) => {
                        if (isChecked) {
                          setPaymentCard(card);
                        }
                      }}
                      label={cardLabel}
                    ></Radio>
                  ) : (
                    cardLabel
                  )
                ) : (
                  <div>
                    <div className="flex items-center gap-2">
                      <span>
                        {card.nickname || 'Card ending'} •••• {card.last4}
                      </span>
                      {icon}
                    </div>
                    <Typography type={'text-sm'}>Disabled until {disabledLabel}</Typography>
                  </div>
                )}
              </div>
            );
          })}
          <div className="flex justify-center">
            <Button
              className="button-primary"
              onClick={() => {
                setSelectionForm('stripeSetup');
              }}
            >
              Add new card
            </Button>
          </div>
        </div>
      )}

      {deleteConfirmModal}
    </>
  );

  return (
    <>
      {selectionForm == 'cardSelect' && <CardSelect />}
      {selectionForm == 'stripeSetup' && (
        <StripeSetupInit userId={userId} close={() => setSelectionForm('cardSelect')} type={'card'} setCard={setCard} />
      )}
      {selectionForm == 'stripeSetupEdit' && paymentCard && (
        <StripeSetupInit
          userId={userId}
          close={() => setSelectionForm('cardSelect')}
          userCard={paymentCard}
          type={'card'}
        />
      )}
    </>
  );
};
